import { isExternalUrl } from './urlUtils';
import { canonicalizeSolanaAppStoreUrl } from '../shared/arweave/publicArweaveGateway';
import { R2_PUBLIC_HOSTS } from '../lib/config';
import type { UploadProviderPreference } from './providerHelpers';

export interface ReusableReleaseApk {
  uri: string;
  size: number;
  sha256: string;
}

export function getReusableR2ReleaseApk({
  uploadProvider,
  sourceUrl,
  fileSize,
  fileHash,
}: {
  uploadProvider: UploadProviderPreference;
  sourceUrl: string;
  fileSize: number;
  fileHash: string;
}): ReusableReleaseApk | null {
  if (uploadProvider !== 'r2' || !Number.isFinite(fileSize) || fileSize <= 0) {
    return null;
  }

  const sha256 = fileHash.trim().toLowerCase();
  if (!/^[a-f0-9]{64}$/.test(sha256)) {
    return null;
  }

  try {
    const uri = canonicalizeSolanaAppStoreUrl(sourceUrl);
    const url = new URL(uri);
    if (
      url.protocol !== 'https:' ||
      !R2_PUBLIC_HOSTS.includes(url.hostname.toLowerCase()) ||
      !/^\/a\/[^/]+\.apk$/i.test(url.pathname)
    ) {
      return null;
    }

    return { uri, size: fileSize, sha256 };
  } catch {
    return null;
  }
}

type TrpcClient = {
  getPresignedUrl: {
    query: (input: {
      url: string;
    }) => Promise<
      | { _tag: 'Left'; left: { message: string } }
      | { _tag: 'Right'; right: { presignedUrl: string } }
    >;
  };
  fetchRemoteFile?: {
    query: (input: { url: string; fileName?: string }) => Promise<
      | { _tag: 'Left'; left: { message: string } }
      | {
          _tag: 'Right';
          right: { data: string; fileName: string; mimeType: string };
        }
    >;
  };
};

interface DownloadRetryContext {
  attempt: number;
  maxAttempts: number;
  delayMs: number;
  error: Error;
}

interface DownloadFileWithPresignedUrlOptions {
  sourceUrl: string;
  fileName: string;
  mimeType: string;
  trpc: TrpcClient;
  timeoutMs?: number;
  maxAttempts?: number;
  onProgress?: (progress: number) => void;
  onRetry?: (context: DownloadRetryContext) => void;
}

const DEFAULT_MAX_DOWNLOAD_ATTEMPTS = 3;
const DEFAULT_DOWNLOAD_TIMEOUT_MS = 5 * 60 * 1000;
const MAX_DOWNLOAD_TIMEOUT_MS = 30 * 60 * 1000;
const DEFAULT_ASSET_DOWNLOAD_TIMEOUT_MS = 60 * 1000;
const DEFAULT_ASSET_DOWNLOAD_ATTEMPTS = 2;
const MIN_EXPECTED_DOWNLOAD_SPEED_BYTES_PER_SECOND = 128 * 1024;
const DOWNLOAD_TIMEOUT_GRACE_MS = 30 * 1000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function extractStatusCode(message: string): number | null {
  const match = message.match(/\b(\d{3})\b/);
  if (!match) {
    return null;
  }
  const parsed = Number.parseInt(match[1], 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function isRetryableStatusCode(statusCode: number): boolean {
  return (
    statusCode === 408 ||
    statusCode === 425 ||
    statusCode === 429 ||
    statusCode === 500 ||
    statusCode === 502 ||
    statusCode === 503 ||
    statusCode === 504
  );
}

function isRetryableDownloadError(error: Error): boolean {
  const message = error.message.toLowerCase();
  if (
    message.includes('timed out') ||
    message.includes('network error') ||
    message.includes('failed to fetch')
  ) {
    return true;
  }

  const statusCode = extractStatusCode(message);
  if (statusCode === null) {
    return false;
  }

  return isRetryableStatusCode(statusCode);
}

function getRetryDelayMs(attempt: number): number {
  const maxBackoffMs = 8000;
  const baseDelayMs = 1000;
  const backoffMs = Math.min(baseDelayMs * 2 ** (attempt - 1), maxBackoffMs);
  const jitterMs = Math.floor(Math.random() * 250);
  return backoffMs + jitterMs;
}

function isHttpsUrl(url: string): boolean {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

async function fetchFileThroughApi({
  sourceUrl,
  fileName,
  mimeType,
  fetchRemoteFile,
}: {
  sourceUrl: string;
  fileName: string;
  mimeType: string;
  fetchRemoteFile: NonNullable<TrpcClient['fetchRemoteFile']>;
}): Promise<File> {
  const result = await fetchRemoteFile.query({
    url: sourceUrl,
    fileName,
  });
  if (result._tag === 'Left') {
    throw new Error(result.left.message);
  }

  const byteString = atob(result.right.data);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i += 1) {
    bytes[i] = byteString.charCodeAt(i);
  }

  return new File([bytes], result.right.fileName || fileName, {
    type: result.right.mimeType || mimeType,
  });
}

function downloadFileFromUrl({
  downloadUrl,
  fileName,
  mimeType,
  timeoutMs,
  onProgress,
}: {
  downloadUrl: string;
  fileName: string;
  mimeType: string;
  timeoutMs: number;
  onProgress?: (progress: number) => void;
}): Promise<File> {
  return new Promise<File>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', downloadUrl, true);
    xhr.responseType = 'blob';
    xhr.timeout = timeoutMs;

    xhr.onprogress = (event) => {
      if (!onProgress || !event.lengthComputable || event.total <= 0) {
        return;
      }
      const percentComplete = Math.round((event.loaded / event.total) * 100);
      onProgress(percentComplete);
    };

    xhr.ontimeout = () => {
      const timeoutMinutes = Math.max(1, Math.ceil(timeoutMs / (60 * 1000)));
      reject(new Error(`Download timed out after ${timeoutMinutes} minutes`));
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const blob = xhr.response as Blob;
        if (blob.size === 0) {
          reject(new Error('Downloaded file is empty'));
          return;
        }

        resolve(new File([blob], fileName, { type: blob.type || mimeType }));
        return;
      }

      reject(
        new Error(`Failed to download file: ${xhr.status} ${xhr.statusText}`),
      );
    };

    xhr.onerror = () => {
      const status = xhr.status > 0 ? `${xhr.status} ${xhr.statusText}` : '0';
      reject(new Error(`Network error while downloading file: ${status}`));
    };

    xhr.send();
  });
}

export function calculateAdaptiveDownloadTimeoutMs(
  fileSizeBytes: number | null | undefined,
): number {
  if (!fileSizeBytes || fileSizeBytes <= 0) {
    return DEFAULT_DOWNLOAD_TIMEOUT_MS;
  }

  const estimatedMs = Math.ceil(
    (fileSizeBytes / MIN_EXPECTED_DOWNLOAD_SPEED_BYTES_PER_SECOND) * 1000 +
      DOWNLOAD_TIMEOUT_GRACE_MS,
  );

  return Math.min(
    Math.max(estimatedMs, DEFAULT_DOWNLOAD_TIMEOUT_MS),
    MAX_DOWNLOAD_TIMEOUT_MS,
  );
}

async function resolveDownloadUrl(
  url: string,
  trpc: TrpcClient,
): Promise<string> {
  const canonicalUrl = canonicalizeSolanaAppStoreUrl(url);
  if (isExternalUrl(canonicalUrl)) {
    return canonicalUrl;
  }

  const presignedResult = await trpc.getPresignedUrl.query({
    url: canonicalUrl,
  });
  if (presignedResult._tag === 'Left') {
    throw new Error(presignedResult.left.message);
  }

  return presignedResult.right.presignedUrl;
}

export async function downloadFileWithPresignedUrl({
  sourceUrl,
  fileName,
  mimeType,
  trpc,
  timeoutMs = DEFAULT_DOWNLOAD_TIMEOUT_MS,
  maxAttempts = DEFAULT_MAX_DOWNLOAD_ATTEMPTS,
  onProgress,
  onRetry,
}: DownloadFileWithPresignedUrlOptions): Promise<File> {
  const boundedTimeoutMs = Math.min(
    Math.max(timeoutMs, 1000),
    MAX_DOWNLOAD_TIMEOUT_MS,
  );
  const boundedMaxAttempts = Math.max(1, maxAttempts);
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= boundedMaxAttempts; attempt += 1) {
    try {
      const downloadUrl = await resolveDownloadUrl(sourceUrl, trpc);
      return await downloadFileFromUrl({
        downloadUrl,
        fileName,
        mimeType,
        timeoutMs: boundedTimeoutMs,
        onProgress,
      });
    } catch (error) {
      const normalizedError = toError(error);
      lastError = normalizedError;

      const shouldRetry =
        attempt < boundedMaxAttempts &&
        isRetryableDownloadError(normalizedError);

      if (!shouldRetry) {
        throw normalizedError;
      }

      const delayMs = getRetryDelayMs(attempt);
      onRetry?.({
        attempt,
        maxAttempts: boundedMaxAttempts,
        delayMs,
        error: normalizedError,
      });
      await delay(delayMs);
    }
  }

  throw lastError ?? new Error('Failed to download file');
}

export async function fetchAssetWithPresignedUrl(
  url: string,
  fileName: string,
  mimeType: string,
  trpc: TrpcClient,
): Promise<File> {
  const canonicalUrl = canonicalizeSolanaAppStoreUrl(url);
  try {
    return await downloadFileWithPresignedUrl({
      sourceUrl: canonicalUrl,
      fileName,
      mimeType,
      trpc,
      timeoutMs: DEFAULT_ASSET_DOWNLOAD_TIMEOUT_MS,
      maxAttempts: DEFAULT_ASSET_DOWNLOAD_ATTEMPTS,
    });
  } catch (error) {
    const { fetchRemoteFile } = trpc;
    if (
      !fetchRemoteFile ||
      !isExternalUrl(canonicalUrl) ||
      !isHttpsUrl(canonicalUrl) ||
      !toError(error)
        .message.toLowerCase()
        .includes('network error while downloading file: 0')
    ) {
      throw error;
    }

    return fetchFileThroughApi({
      sourceUrl: canonicalUrl,
      fileName,
      mimeType,
      fetchRemoteFile,
    });
  }
}

export async function fetchFirstAvailableAssetWithPresignedUrl(
  urls: Array<string | null | undefined>,
  fileName: string,
  mimeType: string,
  trpc: TrpcClient,
): Promise<File> {
  let lastError: unknown;
  const uniqueUrls = Array.from(
    new Set(urls.filter((url): url is string => !!url)),
  );

  for (const url of uniqueUrls) {
    try {
      return await fetchAssetWithPresignedUrl(url, fileName, mimeType, trpc);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error('No asset URL available');
}
